import { useCallback, useEffect, useRef } from "react";

/**
 * Subscribe to a Supabase Realtime broadcast channel scoped to the user.
 * When a "force-logout" event is received the provided signOut callback fires.
 *
 * Returns a `broadcast` function that sends a force-logout event to all OTHER
 * sessions via the same already-subscribed channel — call it before a global
 * sign-out so other devices are kicked immediately.
 *
 * Best-effort: if the broadcast fails, session invalidation on the server
 * side is the real enforcement (other sessions will fail on next token refresh).
 */
export function useForceLogout(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabase: any,
  userId: string | null,
  signOut: () => Promise<void> | void,
): { broadcast: (reason?: string) => Promise<void> } {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const channelRef = useRef<any>(null);
  const subscribedRef = useRef(false);

  useEffect(() => {
    if (!supabase || !userId) return;

    const channel = supabase
      .channel(`force-logout:${userId}`)
      .on("broadcast", { event: "force-logout" }, () => {
        console.warn("[useForceLogout] Received force-logout broadcast — signing out");
        signOut();
      })
      .subscribe((status: string) => {
        if (status === "SUBSCRIBED") {
          subscribedRef.current = true;
        }
      });

    channelRef.current = channel;

    return () => {
      subscribedRef.current = false;
      channelRef.current = null;
      supabase.removeChannel(channel);
    };
  }, [supabase, userId, signOut]);

  const broadcast = useCallback(async (reason = "sign_out_all_devices") => {
    const channel = channelRef.current;
    if (!channel || !subscribedRef.current) return;
    try {
      await channel.send({
        type: "broadcast",
        event: "force-logout",
        payload: { reason },
      });
    } catch {
      // Non-critical — session invalidation is the real enforcement
      console.warn("[useForceLogout] Failed to broadcast force-logout");
    }
  }, []);

  return { broadcast };
}
